import { useContext, useState } from 'react'

import { callApiWithAuth } from 'api/utils'
import moment from 'moment'
import { useRouter } from 'next/router'
import { useIntl } from 'react-intl'
import { AppContext } from 'state/appContext'

import { ReducerAction, TogetherDataContext } from '../'
import { formatPost } from '../api/dataTransform'
import { PATHS } from '../api/paths'
import { PostApi } from '../api/types'
import { getTogetherPath } from '../api/utils'
import { MAX_IMAGE_UPLOADS } from '../interfaces/constants'
import { Post } from '../interfaces/types'
import { PostActions } from '../state/postReducer'
import { safeEncodeURI } from '../utils'
import { getDefaultFailNoti } from '../utils/notification'
import {
  encodePostContent,
  getMentionIdsFromString,
  removeBannedWordWrapper,
  uploadAndGetSrc,
} from '../utils/post'
import { useAuthRequiredAction } from './useAuthRequiredAction'

export type Props = {
  onSuccess?: (res?: any) => void
  onFail?: () => void
}

export const likeUnlikePost = async ({
  id,
  locale,
  isLike,
  dispatch,
}: {
  id: string
  locale: string
  isLike?: boolean
  dispatch?: (action: ReducerAction) => void
}) => {
  if (!id) return
  const res = await callApiWithAuth(
    getTogetherPath(PATHS.POST.LIKE, {
      id,
      _locale: locale,
    }),
    'GET',
  )
  if (res?.status === 1) {
    dispatch &&
      dispatch({
        type: PostActions.update,
        payload: {
          id,
          data: (original: Post) => ({
            isLiked: isLike,
            likeCount: isLike
              ? typeof original?.likeCount === 'number'
                ? original.likeCount + 1
                : 1
              : typeof original?.likeCount === 'number'
              ? original.likeCount - 1
              : 1,
          }),
          ignoreNotFound: true,
        },
      })
  }
  return res
}

export const useLikeUnlikePost = (params?: { authActionWrapper?: any }) => {
  const { locale = 'vi-VN' } = useRouter()
  const { dispatch } = useContext(TogetherDataContext)
  const [loading, setLoading] = useState(false)
  const { formatMessage: f } = useIntl()
  const { pushNotifications } = useContext(AppContext)
  const { authActionWrapper } = useAuthRequiredAction()

  const onLikeUnlike = ({
    id,
    isLike = false,
    onCallback,
  }: {
    id: string
    isLike?: boolean
    onCallback?: (isLike: boolean) => void
  }) => {
    return (params?.authActionWrapper || authActionWrapper)(
      async () => {
        let res
        if (loading) return res
        setLoading(true)
        try {
          res = await likeUnlikePost({
            id,
            locale,
            isLike,
            dispatch,
          })
          if (res?.status !== 1) {
            pushNotifications(getDefaultFailNoti(f))
          } else {
            onCallback?.(isLike)
          }
        } catch {
          pushNotifications(getDefaultFailNoti(f))
        }
        setLoading(false)
        return res
      },
      {
        action: 'like',
      },
    )
  }
  return { loading, onLikeUnlike }
}

export const deletePost = async ({
  id,
  locale,
  dispatch,
}: {
  dispatch?: (action: ReducerAction) => void
  id: string
  locale: string
}) => {
  if (!id) return
  const res = await callApiWithAuth(
    getTogetherPath(PATHS.POST.DELETE, {
      id,
      _locale: locale,
    }),
    'DELETE',
  )
  if (res?.status === 1)
    dispatch &&
      dispatch({
        type: PostActions.remove,
        payload: {
          id,
        },
      })
  return res
}
export const useDeletePost = () => {
  const { locale = 'vi-VN' } = useRouter()
  const { dispatch } = useContext(TogetherDataContext)
  const { authActionWrapper } = useAuthRequiredAction()
  const { pushNotifications } = useContext(AppContext)
  const [loading, setLoading] = useState(false)
  const { formatMessage: f } = useIntl()
  const onDeletePost = (id: string) => {
    return authActionWrapper(async () => {
      let res
      if (loading) return res
      setLoading(true)
      try {
        res = await deletePost({ id, locale, dispatch })
        if (res?.status === 1) {
          pushNotifications({
            title: '',
            message: f({
              id: 'notification.post.delete',
              defaultMessage: 'Đã xóa bài viết',
            }),
            type: 'success',
          })
        } else {
          pushNotifications(getDefaultFailNoti(f))
        }
      } catch {
        pushNotifications(getDefaultFailNoti(f))
      }
      setLoading(false)
      return res
    })
  }
  return { loading, onDeletePost }
}

export type PostFormDataProps = {
  title: string
  description: string
  communityId: string
  imageFiles?: (string | File)[]
  previewUrl?: {
    title?: string
    description?: string
    url?: string
    image?: string
  }
  topicIds: string[]
  locale: string
  isAnonymous?: number
  // admin only
  introUrl?: string
  bannerUrl?: string | File
  scheduleTime?: string
  isAdmin?: boolean
}

export const getPostFormData = async ({
  title,
  description,
  communityId,
  imageFiles,
  previewUrl,
  topicIds,
  locale,
  isAnonymous,

  // admin only
  introUrl,
  bannerUrl,
  scheduleTime,
  isAdmin,
}: PostFormDataProps) => {
  const bodyFormData = new FormData()
  const submitDescription = removeBannedWordWrapper(description)
  const submitTitle = removeBannedWordWrapper(title)
  const mentionIds = getMentionIdsFromString(submitDescription)
  bodyFormData.append('title', submitTitle)
  bodyFormData.append('description', encodePostContent(submitDescription))
  bodyFormData.append('community_id', communityId)
  bodyFormData.append('is_anonymous', isAnonymous ? '1' : '0')

  previewUrl?.title && bodyFormData.append('preview_title', previewUrl.title)
  previewUrl?.description &&
    bodyFormData.append('preview_description', previewUrl.description)
  previewUrl?.url &&
    bodyFormData.append('preview_link', safeEncodeURI(previewUrl.url))
  previewUrl?.image &&
    bodyFormData.append('preview_image', safeEncodeURI(previewUrl.image))

  topicIds?.forEach((topicId) => {
    bodyFormData.append('topics[]', String(topicId || ''))
  })
  if (!!imageFiles?.length) {
    const imageSrcs = await Promise.all(
      imageFiles.map(
        (file, index) =>
          new Promise<string>((resolve) => {
            setTimeout(() => {
              resolve(uploadAndGetSrc(file, locale, isAdmin))
            }, index * 2000)
          }),
      ),
    )
    imageSrcs
      ?.filter(Boolean)
      .slice(0, MAX_IMAGE_UPLOADS)
      .forEach((imageFile, index) => {
        bodyFormData.append(`images[${index}]`, imageFile)
      })
  } else {
    bodyFormData.append(`images`, '')
  }
  mentionIds?.forEach((mentionId) => {
    bodyFormData.append(`mention_ids[]`, mentionId)
  })

  // Admin only
  bodyFormData.append('intro_url', introUrl || '')
  bodyFormData.append('banner', bannerUrl || '')
  if (scheduleTime) {
    bodyFormData.append('post_type', 'scheduled')
    bodyFormData.append(
      'schedule_datetime',
      moment(scheduleTime, 'YYYY-MM-DD HH:mm:ss').toISOString(),
    )
  }
  return bodyFormData
}
export const createPost = async ({
  formData,
  locale,
  dispatch,
}: {
  formData: FormData
  locale: string
  dispatch?: (action: ReducerAction) => void
}) => {
  const res:
    | {
        messages?:
          | {
              moderation: {
                type: string
                words: string[]
              }
            }
          | string[]
        data?: PostApi | null
        status: number
      }
    | undefined = await callApiWithAuth(
    getTogetherPath(PATHS.POST.CREATE, {
      _locale: locale,
    }),
    'POST',
    {
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  )
  const data = res?.data
  if (data?.slug && dispatch) {
    const res2 = await callApiWithAuth(
      getTogetherPath(PATHS.POST.DETAIL_BY_ID_OR_SLUG, {
        idSlug: data.slug,
        _locale: locale,
      }),
      'GET',
    )
    if (res2?.data?.id) {
      const newPost = formatPost(res2.data)
      newPost.id &&
        dispatch({
          type: PostActions.update,
          payload: {
            data: newPost,
          },
        })
    }
  }
  return res
}

export const editPost = async ({
  id,
  formData,
  locale,
  dispatch,
}: {
  id: string
  formData: FormData
  locale: string
  dispatch?: (action: ReducerAction) => void
}) => {
  const res:
    | {
        messages?:
          | {
              moderation: {
                type: string
                words: string[]
              }
            }
          | string[]
        data?: PostApi | null
        status: number
      }
    | undefined = await callApiWithAuth(
    getTogetherPath(PATHS.POST.UPDATE, {
      id,
      _locale: locale,
    }),
    'POST',
    {
      data: formData,
    },
  )
  const data = res?.data
  if (data?.id && dispatch) {
    const newPost = formatPost(data)
    dispatch({
      type: PostActions.update,
      payload: {
        data: newPost,
      },
    })
  }
  return res
}

export const followUnfollowPost = ({
  id,
  locale,
}: {
  id: string
  locale: string
  followValue?: boolean
}) => {
  return callApiWithAuth(
    getTogetherPath(PATHS.POST.FOLLOW, {
      id,
      _locale: locale,
    }),
    'GET',
  )
}
export const useFollowUnfollowPost = () => {
  const { formatMessage: f } = useIntl()
  const { locale = 'vi-VN' } = useRouter()
  const { pushNotifications } = useContext(AppContext)
  const [loading, setLoading] = useState(false)
  const { authActionWrapper } = useAuthRequiredAction()
  const onFollowUnfollowPost = ({
    id,
    followValue,
  }: {
    id: string
    followValue: boolean
  }) => {
    return authActionWrapper(async () => {
      let res
      if (loading) return res
      setLoading(true)
      try {
        res = await followUnfollowPost({ id, locale, followValue })
        if (res) {
          pushNotifications({
            message: followValue ? 'Notification on' : 'Notification off',
            type: followValue ? 'success' : 'danger',
            displayIcon: followValue
              ? '/svg/icon-bell-on.svg'
              : '/svg/icon-bell-off.svg',
          })
        }
      } catch {
        pushNotifications(getDefaultFailNoti(f))
      }
      setLoading(false)
      return res
    })
  }
  return { loading, onFollowUnfollowPost }
}
export const reportPost = ({
  id,
  reason,
  locale,
}: {
  id: string
  reason: string
  locale: string
}) => {
  const formData = new FormData()
  formData.append('reason', reason)
  return callApiWithAuth(
    getTogetherPath(PATHS.POST.REPORT, {
      _locale: locale,
      id,
    }),
    'POST',
    {
      data: formData,
    },
  )
}
export const useReportPost = () => {
  const { formatMessage: f } = useIntl()
  const { locale = 'vi-VN' } = useRouter()
  const { pushNotifications } = useContext(AppContext)
  const [loading, setLoading] = useState(false)
  const { authActionWrapper } = useAuthRequiredAction()
  const onReportPost = ({ id, reason }: { id: string; reason: string }) => {
    return authActionWrapper(
      async () => {
        let res
        if (loading) return res
        setLoading(true)
        try {
          res = await reportPost({ id, locale, reason })
          if (res?.status === 1) {
            pushNotifications({
              message: 'Post reported',
              type: 'success',
            })
          }
        } catch {
          pushNotifications(getDefaultFailNoti(f))
        }
        setLoading(false)
        return res
      },
      {
        action: 'report-post',
      },
    )
  }
  return { loading, onReportPost }
}
